import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

export type TipoToast = 'ok' | 'erro' | 'aviso' | 'info';

export interface Toast {
  id: number;
  tipo: TipoToast;
  texto: string;
  codigo?: string;
  acao?: { rotulo: string; executar: () => void };
  duracaoMs?: number;
}

interface ApiToast {
  mostrar: (t: Omit<Toast, 'id'>) => void;
}

const Contexto = createContext<ApiToast>({ mostrar: () => undefined });

export const useToast = () => useContext(Contexto);

const ICONES = { ok: CircleCheck, erro: CircleAlert, aviso: TriangleAlert, info: Info } as const;

/** role="status" (erro: "alert"); erro não some sozinho (07 §9). */
export function ProvedorToast({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  const fechar = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const mostrar = useCallback((t: Omit<Toast, 'id'>) => {
    const id = ++seq.current;
    setToasts((lista) => [...lista.slice(-3), { ...t, id }]);
    if (t.tipo !== 'erro') setTimeout(() => fechar(id), t.duracaoMs ?? 4000);
  }, [fechar]);
  const api = useMemo(() => ({ mostrar }), [mostrar]);
  return (
    <Contexto.Provider value={api}>
      {children}
      <div className="toasts">
        {toasts.map((t) => {
          const Icone = ICONES[t.tipo];
          return (
            <div key={t.id} className="toast folha" data-tipo={t.tipo} role={t.tipo === 'erro' ? 'alert' : 'status'} data-testid="toast">
              <Icone size={18} strokeWidth={1.75} style={{ color: `var(--${t.tipo === 'erro' ? 'erro' : t.tipo === 'ok' ? 'ok' : t.tipo === 'aviso' ? 'aviso' : 'info'})`, flex: 'none', marginTop: 1 }} aria-hidden="true" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div>{t.texto}</div>
                {t.codigo && <div className="mono texto-3" style={{ fontSize: 12, userSelect: 'all' }}>{t.codigo}</div>}
                {t.acao && <button type="button" className="botao botao-fantasma" data-tamanho="p" onClick={t.acao.executar}>{t.acao.rotulo}</button>}
              </div>
              <button type="button" className="botao botao-fantasma botao-icone" data-tamanho="p" aria-label="Fechar aviso" onClick={() => fechar(t.id)} style={{ width: 28, height: 28 }}>
                <X size={16} strokeWidth={1.75} />
              </button>
            </div>
          );
        })}
      </div>
    </Contexto.Provider>
  );
}
